import { GameAnalytics } from '../../../analytics/GameAnalytics';
import { AnalyticsEventNames } from '../../../analytics/AnalyticsEventNames';
import { StoryProgressManager } from '../saveData/StoryProgressManager';
import { CoreController } from '../../CoreController';
import { Events } from '../../Events';
import { SceneManager } from '../SceneManager';
import { TextStyles } from '../../../utils/TextStyles';
import { animateMessage } from '../../../utils/Animations';
import { AbstractPopUp, AbstractPopUpParams } from '../../../popUps/AbstractPopUp';
import { PopUpManager, PopUpName } from '../PopUpManager';
import TextStyle = PIXI.TextStyle;

export type AdInstanceID = string;

export interface MoDEntryPointData {
    adId: string;
}

export enum Type {
    TYPE_O = 'TYPE_O',
    TYPE_A = 'TYPE_A',
}

export enum RewardedPlacement {
    continue = 'continue',
    bubble_moves = 'bubble_moves',
    retry = 'retry',
    no_lives = 'no_lives',
    double_reward = 'double_reward',
}

export enum InterstitialPlacement {
    level_won = 'level_won',
}

export interface RewardedAd {
    loadAsync: () => Promise<any>;
    showAsync: () => Promise<any>;
    $1: {
        adInstanceID: AdInstanceID;
    };
}

export interface InterstitialAd {
    loadAsync: () => Promise<any>;
    showAsync: () => Promise<any>;
    $1: {
        adInstanceID: AdInstanceID;
    };
}

export class AbstractAdManager {
    protected static _instance: AbstractAdManager;
    protected rewardedOrganic: string = '1270477553338225_1317861531933160';
    protected rewardedAd: string = '1270477553338225_1345039842548662';

    protected interstitialOrganic: string = '1270477553338225_1345038562548790';
    protected interstitialAd: string = '1270477553338225_1345038709215442';

    protected bannerAd: string = '1270477553338225_1492598127792832';
    protected bannerOrganic: string = '1270477553338225_1492598347792810';

    protected rewardedLoading: AdInstanceID[] = [];
    protected rewardedLoaded: RewardedAd[] = [];
    protected pendingRewarded: () => any;

    protected interstitialLoading: AdInstanceID[] = [];
    protected interstitialLoaded: InterstitialAd[] = [];

    protected type: Type;

    protected requestTimestamps: number[] = [];
    protected preAdPopup: AbstractPopUp;
    protected _hasBannerAd: boolean = false;

    public async init(): Promise<void> {
        await StoryProgressManager.getInstance().getType().then((data: any) => {
            if (data && data.type !== undefined) {
                if (data.type === Type.TYPE_A) {
                    this.type = data.type;
                } else {
                    this.type = Type.TYPE_O;
                }
            } else {
                if (CoreController.getInstance().getCurrentLevel() > 1) {
                    this.setTypeO();
                } else {
                    this.setTypeOnFirstSession();
                }
            }
        });
    }

    public getType(): Type {
        return this.type;
    }

    protected setTypeOnFirstSession(): void {
        const entryPointData: MoDEntryPointData = FBInstant.getEntryPointData();
        if (!entryPointData) {
            this.setTypeO();
        } else {
            if (entryPointData.adId) {
                this.setTypeA();
            }
        }
    }

    protected setTypeA(): void {
        this.type = Type.TYPE_A;
        FBInstant.player
            .setDataAsync({type: 'TYPE_A'});
    }

    protected setTypeO(): void {
        this.type = Type.TYPE_O;
        FBInstant.player
            .setDataAsync({type: 'TYPE_O'});
    }

    public static getInstance(): AbstractAdManager {
        return this._instance || (this._instance = new this());
    }

    public showBannerAd(): void {
        if (!this._hasBannerAd) {
            FBInstant.loadBannerAdAsync(
                this.getBannerPlacement()
            ).then(() => {
                GameAnalytics.getInstance().logDesign(AnalyticsEventNames.banner_ad_shown);
                this._hasBannerAd = true;
            });
        }
    }

    public changeBannerAd(): void {
        if (this._hasBannerAd) {
            FBInstant.hideBannerAdAsync().then((): void => {
                this._hasBannerAd = false;
                this.showBannerAd();
            });
        } else {
            this.showBannerAd();
        }
    }

    public hideBannerAd(): void {
        if (!this._hasBannerAd) {
            FBInstant.hideBannerAdAsync().then((): void => {
                this._hasBannerAd = false;
            });
        }
    }

    public preloadInterstitialAd(amount: number): void {
        for (let i: number = 0; i < amount; i++) {
            if (this.interstitialLoaded.length < 2 && this.interstitialLoading.length < 1) {
                this.loadInterstitialAd(this.onInterstitialPreloaded.bind(this), this.onErrorPreloadingInterstitial.bind(this));
            }
        }
    }

    public preloadRewardedAd(amount: number): void {
        for (let i: number = 0; i < amount; i++) {
            if (this.rewardedLoaded.length < 2 && this.rewardedLoading.length < 1) {
                this.loadRewardedAd(this.onRewardedPreloaded.bind(this), this.onErrorPreloadingRewarded.bind(this));
            }
        }
    }

    protected onErrorPreloadingRewarded(err: Error, result: RewardedAd): void {
        const adInstanceID: AdInstanceID = result.$1.adInstanceID;
        this.removeRewardedAdFromList(adInstanceID, this.rewardedLoading);
    }

    protected onRewardedPreloaded(result: RewardedAd): void {
        const adInstanceID: AdInstanceID = result.$1.adInstanceID;
        this.removeRewardedAdFromList(adInstanceID, this.rewardedLoading);
    }

    protected onErrorPreloadingInterstitial(err: Error, result: RewardedAd): void {
        const adInstanceID: AdInstanceID = result.$1.adInstanceID;
        this.removeRewardedAdFromList(adInstanceID, this.interstitialLoading);
    }

    protected onInterstitialPreloaded(result: InterstitialAd): void {
        const adInstanceID: AdInstanceID = result.$1.adInstanceID;
        this.removeInterstitialAdFromList(adInstanceID, this.interstitialLoading);
    }

    protected removeRewardedAdFromList(adInstanceID: AdInstanceID, array: AdInstanceID[]): void {
        const index: number = this.rewardedLoading.indexOf(adInstanceID);
        if (index > -1) {
            this.rewardedLoading.splice(index, 1);
        }
    }

    protected removeInterstitialAdFromList(adInstanceID: AdInstanceID, array: AdInstanceID[]): void {
        const index: number = this.interstitialLoading.indexOf(adInstanceID);
        if (index > -1) {
            this.interstitialLoading.splice(index, 1);
        }
    }

    protected loadRewardedAd(
        onLoaded: (result: RewardedAd) => any,
        onError: (error: Error, ad?: RewardedAd) => void,
        onLoading?: () => any
    ): void {
        let ad: RewardedAd = null;
        FBInstant.getRewardedVideoAsync(
            this.getRewardedPlacement(),
        ).then(async (rewardedVideo: any) => {
            ad = rewardedVideo;
            this.rewardedLoading.push(ad.$1.adInstanceID);
            if (onLoading) {
                onLoading();
            }
            this.requestTimestamps.push(Date.now());

            if (this.checkThrottle()) {
                const error: Error = new Error('ADS THROTTLED');
                onError(error, ad);
            } else {
                if (onLoading) {
                    onLoading();
                }
                return ad.loadAsync().then(() => {
                    // Ad loaded
                    this.rewardedLoaded.push(ad);
                    this.removeInterstitialAdFromList(ad.$1.adInstanceID, this.rewardedLoading);
                    onLoaded(ad);
                }).catch((error: Error) => {
                    this.removeInterstitialAdFromList(ad.$1.adInstanceID, this.rewardedLoading);
                    onError(error, ad);
                });
            }
        });
    }

    public showRewardedAd(
        onAdCompleted: () => any,
        onFail: (err: Error) => any,
        placement?: RewardedPlacement,
        onLoading?: () => any,
        noRequestLog: boolean = false
    ): void {
        let ad: RewardedAd;
        if (!noRequestLog) {
            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.rewarded_request + placement, false, true);
            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.rewarded_request, false, true);
        }
        // REWARDED LOADED
        if (this.rewardedLoaded.length > 0) {
            ad = this.rewardedLoaded.pop();
            if (ad) {
                this.showPreAd((): void => {
                    this.pendingRewarded = null;
                    GameAnalytics.getInstance().logDesign(AnalyticsEventNames.rewarded_show + placement, false, true);
                    GameAnalytics.getInstance().logDesign(AnalyticsEventNames.rewarded_show, false, true);
                    if (!GameAnalytics.getInstance().isTester()) {
                        ad.showAsync().then((): void => {
                            onAdCompleted();
                            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.rewarded_impression + placement, false, true);
                            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.rewarded_impression, false, true);
                            this.preloadRewardedAd(1);
                        }).catch((err: any): void => {
                            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.rewarded_fail + '_' + placement, false, true);
                            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.rewarded_fail, false, true);
                            this.preloadRewardedAd(1);
                            onFail(err);
                        });
                    } else {
                        const rnd: number = Math.random();
                        if (rnd < 0.5) {
                            onFail(new Error('RANDOM FAIL'));
                        } else {
                            onAdCompleted();
                        }
                    }
                });
            } else {
                onFail(new Error('NO_AD'));
                GameAnalytics.getInstance().logDesign(AnalyticsEventNames.rewarded_fail + '_' + placement, false, true);
                GameAnalytics.getInstance().logDesign(AnalyticsEventNames.rewarded_fail, false, true);
            }
        } else {
            this.loadRewardedAd(
                (): void => {
                    this.showRewardedAd(onAdCompleted, onFail, placement, onLoading, true);
                },
                onFail,
                onLoading
            );
        }
    }

    protected getRewardedPlacement(): string {
        let placement: string = this.rewardedOrganic;
        if (this.type === Type.TYPE_A) {
            placement = this.rewardedAd;
        }
        return placement;
    }

    protected getInterstitialPlacement(): string {
        let placement: string = this.interstitialOrganic;
        if (this.type === Type.TYPE_A) {
            placement = this.interstitialAd;
        }
        return placement;
    }

    protected getBannerPlacement(): string {
        let placement: string = this.bannerOrganic;
        if (this.type === Type.TYPE_A) {
            placement = this.bannerAd;
        }
        return placement;
    }

    public showInterstitialAd(
        onAdCompleted: () => any,
        placement: InterstitialPlacement,
        noRequestLog: boolean = false,
        onLoading?: () => any
    ): void {
        let ad: InterstitialAd;
        if (!noRequestLog) {
            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.interstitial_request + placement, false, true);
            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.interstitial_request, false, true);
        }
        if (!CoreController.getInstance().getManager().hasNoAdsPass()) {
            // AD LOADED
            if (this.interstitialLoaded.length > 0) {
                ad = this.interstitialLoaded.pop();
                if (ad) {
                    this.showPreAd((): void => {
                        if (!GameAnalytics.getInstance().isTester()) {
                            ad.showAsync().then((): void => {
                                onAdCompleted();
                                dispatchEvent(new CustomEvent(Events.interstitialShown));
                                GameAnalytics.getInstance().logDesign(AnalyticsEventNames.interstitial_show + placement, false, true);
                                GameAnalytics.getInstance().logDesign(AnalyticsEventNames.interstitial_show, false, true);
                                this.preloadInterstitialAd(1);
                            }).catch((err: any): void => {
                                this.preloadInterstitialAd(1);
                                onAdCompleted();
                            });
                        } else {
                            onAdCompleted();
                            dispatchEvent(new CustomEvent(Events.interstitialShown));
                        }
                    });
                }
            } else {
                // NO REWARDED LOADED, NOT PRELOADING
                if (GameAnalytics.getInstance().isTester()) {
                    this.showPreAd((): void => {
                        onAdCompleted();
                        dispatchEvent(new CustomEvent(Events.interstitialShown));
                    });
                } else {
                    this.loadInterstitialAd(
                        (): void => {
                            dispatchEvent(new CustomEvent(Events.stopLoadingAnimation));
                            this.showInterstitialAd(onAdCompleted, placement);
                        },
                        onAdCompleted,
                        onLoading
                    );
                }
            }
        } else {
            onAdCompleted();
        }
    }

    protected loadInterstitialAd(
        onLoaded: (result: InterstitialAd) => any,
        onError: (error: Error, ad?: InterstitialAd) => void,
        onLoading?: () => any
    ): void {
        let ad: InterstitialAd = null;
        FBInstant.getInterstitialAdAsync(
            this.getInterstitialPlacement(),
        ).then(async (interstitialAd: any) => {
            ad = interstitialAd;
            this.interstitialLoading.push(ad.$1.adInstanceID);
            if (onLoading) {
                onLoading();
            }
            this.requestTimestamps.push(Date.now());
            if (this.checkThrottle()) {
                dispatchEvent(new CustomEvent(Events.stopLoadingAnimation));
                const error: Error = new Error('ADS THROTTLED');
                onError(error, ad);
            } else {
                if (onLoading) {
                    onLoading();
                }
                return ad.loadAsync().then(() => {
                    // Ad loaded
                    this.interstitialLoaded.push(ad);
                    this.removeInterstitialAdFromList(ad.$1.adInstanceID, this.interstitialLoading);
                    dispatchEvent(new CustomEvent(Events.stopLoadingAnimation));
                    onLoaded(ad);
                }).catch((error: Error) => {
                    this.removeInterstitialAdFromList(ad.$1.adInstanceID, this.interstitialLoading);
                    dispatchEvent(new CustomEvent(Events.stopLoadingAnimation));
                    onError(error, ad);
                });
            }
        }).catch((error: Error) => {
            onError(error, ad);
        });
    }

    protected checkThrottle(): boolean {
        const limit: number = 3;
        const interval: number = 60 * 1000;
        let loadedInLastInterval: number = 0;
        const currentTime: number = Date.now();

        this.requestTimestamps.forEach((timestamp: number): void => {
            if (currentTime - timestamp < interval) {
                loadedInLastInterval++;
            }
        });

        console.log('LOADED: ' + loadedInLastInterval);
        if (loadedInLastInterval <= limit) {
            return false;
        } else {
            if (GameAnalytics.getInstance().isTester()) {
                const style: TextStyle = TextStyles.messageText;
                const message: PIXI.Text = new PIXI.Text('AU FOST CERUTE ' + loadedInLastInterval + ' RECLAME IN ' + limit + ' MINUTE!!!', style);
                message.anchor.set(0.5, 0.5);
                message.position.set(SceneManager.gameWidth / 2, SceneManager.gameHeight / 2 + 250);
                SceneManager.getInstance().stage.addChild(message);
                animateMessage(message, 1.5, 1, 1);
            }
            GameAnalytics.getInstance().logDesign(AnalyticsEventNames.ads_throttled);
            return true;
        }
    }

    public showPreAd(callback: () => any): void {
        if (!this.preAdPopup) {
            const params: AbstractPopUpParams = {
                noDefaultAnimations: true,
                noBackground: true,
                noCloseButton: true,
            };
            this.preAdPopup = PopUpManager.getInstance().getView(PopUpName.PreAdPopup, params);
        }
        this.preAdPopup.show(true);
        setTimeout((): void => {
            this.preAdPopup.hide(true);
            callback();
        }, 3000);
    }

    public hasBannerAd(): boolean {
        return this._hasBannerAd;
    }
}